import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { InsertableValue } from '@xmedical/core';

/**
 * One piece of a string that has been split around its placeholders. `display` is what gets rendered:
 * the badge label for a placeholder, the literal text otherwise.
 */
export interface PlaceholderSegment {
  display: string;
  isPlaceholder: boolean;
}

/**
 * Matches both placeholder forms the editor accepts: `{{Patient.FullName}}` and the legacy
 * `--Patient.FullName--`. Kept identical to the expression in MatContenteditableTextAreaComponent so the
 * two never disagree about what counts as a placeholder.
 */
const PLACEHOLDER_PATTERN = /{{\s*([\w\s.]+)\s*}}|--([\w\s.]+)--/g;

/**
 * Splits text into literal and placeholder segments.
 *
 * A placeholder that is not in insertableValues stays as literal text — an unknown or stale token has to
 * remain visible rather than disappear or render as a badge with nothing behind it.
 *
 * That deliberately includes whitespace variants. `{{Patient.FullName }}` captures the trailing space, so
 * the lookup misses and it falls through to literal text — which is correct, not a bug. The backend
 * substitutes by exact string replace against the canonical token (PracticePatient.SubstitutePlaceholderValues
 * iterates its placeholder table calling text.Replace with the literal `{{Patient.FullName}}`), so a
 * whitespace variant is never substituted and prints as raw braces on the report. Badging it here would
 * tell the template author it resolves when it does not, and they would not find out until they saw the
 * printed page. Do not "fix" this by trimming the captured name.
 */
export function splitIntoPlaceholderSegments(text: string, insertableValues: InsertableValue[]): PlaceholderSegment[] {
  if (!text) return [];

  const segments: PlaceholderSegment[] = [];
  let lastIndex = 0;

  // exec in a loop rather than matchAll so lastIndex is explicit — the regex is module-level and stateful.
  PLACEHOLDER_PATTERN.lastIndex = 0;
  let match = PLACEHOLDER_PATTERN.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({ display: text.slice(lastIndex, match.index), isPlaceholder: false });
    }

    const name = match[1] || match[2];
    const known = insertableValues.find(value => value.value === `{{${name}}}`);
    segments.push(known
      ? { display: known.display, isPlaceholder: true }
      : { display: match[0], isPlaceholder: false });

    lastIndex = match.index + match[0].length;
    match = PLACEHOLDER_PATTERN.exec(text);
  }

  if (lastIndex < text.length) {
    segments.push({ display: text.slice(lastIndex), isPlaceholder: false });
  }

  return segments;
}

/**
 * Read-only rendering of a template string, drawing its placeholders as badges.
 *
 * The badge used to exist only inside MatContenteditableTextAreaComponent, so the same value looked like a
 * first-class token while being edited and like raw `{{braces}}` everywhere it was displayed — the report
 * builder canvas and print preview both showed the unresolved text. This is the display half.
 *
 * Renders segments as spans rather than injecting HTML: label and title text is practice-authored, and
 * building it into markup would need sanitising.
 */
@Component({
  selector: 'xui-placeholder-text',
  templateUrl: './placeholder-text.component.html',
  styleUrls: ['./placeholder-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaceholderTextComponent {
  text = input<string>('');
  insertableValues = input<InsertableValue[]>([]);

  readonly segments = computed(() => splitIntoPlaceholderSegments(this.text(), this.insertableValues()));
}
